export type WalletErrorCode =
  | "ERR_WALLET_POPUP_BLOCKED"
  | "ERR_WALLET_CANCEL_ACTION"
  | "ERR_WALLET_WEBAUTHN_TIMEOUT_OR_CANCELLED"

const walletErrorMapping: Record<string, Record<string, WalletErrorCode>> = {
  MeteorActionError: {
    "Couldn't open popup window to complete wallet action":
      "ERR_WALLET_POPUP_BLOCKED",
    "User cancelled the action": "ERR_WALLET_CANCEL_ACTION",
  },
  Error: {
    "Modal closed": "ERR_WALLET_CANCEL_ACTION",
  },
  WalletConnectionError: {
    "The user rejected connection.": "ERR_WALLET_CANCEL_ACTION",
  },
  NotAllowedError: {
    "The operation either timed out or was not allowed. See: https://www.w3.org/TR/webauthn-2/#sctn-privacy-considerations-client.":
      "ERR_WALLET_WEBAUTHN_TIMEOUT_OR_CANCELLED",
  },
}

/** Error names that always map to a code regardless of message */
const walletErrorNameMapping: Record<string, WalletErrorCode> = {
  UserRejectedRequestError: "ERR_WALLET_CANCEL_ACTION",
}

/**
 * Extracts a standardized wallet error code from an error object
 * @param error - The error to analyze
 * @param fallback - Default value if no matching error code found
 * @returns Either a wallet error code or the fallback value
 */
export function extractWalletErrorCode<T>(
  error: unknown,
  fallback: T
): T | WalletErrorCode {
  if (error instanceof Error) {
    const nameCode = walletErrorNameMapping[error.name]
    if (nameCode !== undefined) {
      return nameCode
    }

    const code = walletErrorMapping[error.name]?.[error.message]
    if (code !== undefined) {
      return code
    }

    // Recurse into cause chain first — a wrapped error may carry a more
    // specific code deeper in the chain.
    if (error.cause !== undefined) {
      const causeResult = extractWalletErrorCode(error.cause, null)
      if (causeResult !== null) return causeResult
    }

    // Message-based fallback: catch unrecognized cancellation variants.
    // Only use keywords that are unambiguously user-initiated — broad terms
    // like "reject"/"closed"/"refused" can match real errors (e.g.
    // "connection refused") and would silently swallow them.
    const msg = error.message.toLowerCase()
    if (
      msg.includes("cancel") ||
      msg.includes("dismissed") ||
      msg.includes("aborted")
    ) {
      return "ERR_WALLET_CANCEL_ACTION"
    }

    return fallback
  }

  return fallback
}
